/**
 * FileUploadDropzone Component
 *
 * Drag-and-drop file upload zone for CSV files.
 * Supports click-to-browse and drag-and-drop.
 *
 * @module ImportExportTab/components/FileUploadDropzone
 */

import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, Paper } from '@mui/material';
import { CloudUpload as UploadIcon } from '@mui/icons-material';

/**
 * FileUploadDropzone Component
 *
 * @param {Object} props
 * @param {Function} props.onFileSelect - Callback when file is selected
 * @param {string} [props.accept=".csv"] - Accepted file types
 * @param {number} [props.maxSize=10485760] - Max file size in bytes (default 10MB)
 * @param {boolean} [props.disabled=false] - Disable the dropzone
 * @returns {JSX.Element}
 */
function FileUploadDropzone({ onFileSelect, accept = '.csv', maxSize = 10485760, disabled = false }) {
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Handle file selection
   */
  const handleFile = useCallback((file) => {
    setError(null);

    // Validate file type
    if (!file.name.toLowerCase().endsWith('.csv')) {
      setError('Please select a CSV file');
      return;
    }

    // Validate file size
    if (file.size > maxSize) {
      setError(`File size must be less than ${(maxSize / 1048576).toFixed(0)}MB`);
      return;
    }

    // Call parent callback
    onFileSelect(file);
  }, [onFileSelect, maxSize]);

  /**
   * Handle drag over
   */
  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) {
      setIsDragging(true);
    }
  }, [disabled]);

  /**
   * Handle drag leave
   */
  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  /**
   * Handle drop
   */
  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (disabled) return;

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      handleFile(files[0]);
    }
  }, [disabled, handleFile]);

  /**
   * Handle file input change
   */
  const handleInputChange = useCallback((e) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFile(files[0]);
    }
  }, [handleFile]);

  /**
   * Handle click on dropzone
   */
  const handleClick = useCallback(() => {
    if (!disabled) {
      document.getElementById('csv-file-input').click();
    }
  }, [disabled]);

  return (
    <Box>
      <Paper
        elevation={0}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleClick}
        sx={{
          p: 4,
          textAlign: 'center',
          cursor: disabled ? 'not-allowed' : 'pointer',
          border: '2px dashed',
          borderColor: error ? 'error.main' : isDragging ? '#7C3AED' : 'divider',
          backgroundColor: isDragging ? 'rgba(124, 58, 237, 0.05)' : disabled ? 'action.disabledBackground' : 'background.paper',
          transition: 'all 0.2s',
          opacity: disabled ? 0.6 : 1,
          '&:hover': {
            borderColor: disabled ? 'divider' : '#7C3AED',
            backgroundColor: disabled ? 'action.disabledBackground' : 'rgba(124, 58, 237, 0.02)'
          }
        }}
      >
        <input
          id="csv-file-input"
          type="file"
          accept={accept}
          onChange={handleInputChange}
          disabled={disabled}
          style={{ display: 'none' }}
          aria-label="Drop CSV file here or click to browse"
        />

        <UploadIcon
          sx={{
            fontSize: 48,
            color: error ? 'error.main' : isDragging ? '#7C3AED' : 'text.secondary',
            mb: 2
          }}
        />

        <Typography variant="h6" gutterBottom>
          {isDragging ? 'Drop file here' : 'Drop CSV file here or click to browse'}
        </Typography>

        <Typography variant="body2" color="text.secondary">
          Accepted format: CSV • Max size: {(maxSize / 1048576).toFixed(0)}MB
        </Typography>

        {error && (
          <Typography variant="body2" color="error" sx={{ mt: 1 }}>
            {error}
          </Typography>
        )}
      </Paper>
    </Box>
  );
}

FileUploadDropzone.propTypes = {
  onFileSelect: PropTypes.func.isRequired,
  accept: PropTypes.string,
  maxSize: PropTypes.number,
  disabled: PropTypes.bool
};

export default FileUploadDropzone;
